import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../hooks/useScrollReveal';

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" /></svg>
);
const BoltIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
);
const CoinIcon = () => (
  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 6v12M8 10h8M8 14h8" /></svg>
);
const DocIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>
);

const services = [
  { icon: <ShieldIcon />, title: 'Deter Thieves', body: 'Professional DMCA filing makes stealing your content more difficult. We handle everything — platform notifications, legal documentation, follow-ups.' },
  { icon: <BoltIcon />, title: 'Fast Takedowns', body: 'What takes weeks to handle alone takes days with us. Expert submissions to TikTok, Facebook, and YouTube legal teams.' },
  { icon: <CoinIcon />, title: 'Save Time & Money', body: 'No lawyer fees. No expensive legal consultations. We handle your takedowns for one affordable fee.' },
  { icon: <DocIcon />, title: 'Expert Documentation', body: 'We preserve timestamps, metadata, and proof of ownership. Every filing is verified and compliant with international copyright law.' },
  { icon: <EyeIcon />, title: 'Ongoing Monitoring', body: 'Track repeat infringers. We maintain detailed records and help enforce account suspensions to prevent future violations.' },
  { icon: <CheckIcon />, title: 'Peace of Mind', body: 'Full compliance with DMCA regulations and Somalia Copyright Law. Your content is protected by professionals.' },
];

const Homepage: React.FC = () => {
  const scrollRef = useScrollReveal();

  return (
    <div className="homepage" ref={scrollRef}>
      {/* Hero */}
      <section className="hero reveal-section">
        <div className="hero-inner">
          <span className="eyebrow reveal">Copyright Protection</span>
          <h1 className="reveal">
            Protect Your Content.<br />
            <em>Reclaim</em> Your Rights.
          </h1>
          <p className="hero-body reveal">
            Professional DMCA takedown protection for Somali content creators. Expert-backed legal filings with fast results.
          </p>
          <div className="reveal">
            <Link to="/contact" className="btn-primary">
              Report Stolen Content <span className="btn-arrow">&rarr;</span>
            </Link>
          </div>
          <p className="hero-subtext reveal">Trusted by thousands of Somali creators across every major platform</p>
        </div>
      </section>

      {/* Problem */}
      <section className="problem reveal-section">
        <div className="container">
          <span className="eyebrow reveal">The Problem</span>
          <h2 className="reveal">Is Your Content Being Stolen?</h2>
          <p className="problem-body reveal">
            Every day, Somali creators lose earnings to content theft on TikTok, Facebook, and YouTube. Stolen videos. Copied uploads. Unauthorized reposts. Without action, thieves keep profiting from your work.
          </p>
          <p className="highlight-serif reveal">It's time to take back control.</p>
        </div>
      </section>

      {/* Services */}
      <section className="services reveal-section">
        <div className="services-header">
          <span className="eyebrow reveal">Why Us</span>
          <h2 className="reveal">Why Choose Somali Copyright Agency?</h2>
        </div>
        <div className="services-grid">
          {services.map((s) => (
            <div className="service-card reveal" key={s.title}>
              <div className="service-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="process reveal-section">
        <div className="process-header">
          <span className="eyebrow reveal">How It Works</span>
          <h2 className="reveal">Simple 3-Step Process</h2>
        </div>
        <div className="process-steps">
          <div className="process-step reveal">
            <div className="step-dot">01</div>
            <h4>Submit Your Report</h4>
            <p>Provide your original content URL, the stolen post link, and upload proof of your creation. Takes just 5 minutes.</p>
          </div>
          <div className="process-step reveal">
            <div className="step-dot">02</div>
            <h4>We Handle Everything</h4>
            <p>Our team prepares professional DMCA documentation and submits directly to platform legal teams on your behalf.</p>
          </div>
          <div className="process-step reveal">
            <div className="step-dot">03</div>
            <h4>Content Removed</h4>
            <p>Platforms respond within days. Stolen content removed. Your earnings protected. You stay updated throughout.</p>
          </div>
        </div>
        <div className="process-cta reveal">
          <Link to="/contact" className="btn-primary">
            Get Started Today <span className="btn-arrow">&rarr;</span>
          </Link>
        </div>
      </section>

      {/* About + Stats */}
      <section className="about reveal-section">
        <div className="about-grid">
          <div className="about-content">
            <span className="eyebrow reveal">About Us</span>
            <h2 className="reveal">Protecting Somali Creators Since Day One</h2>
            <p className="reveal">
              We're dedicated to protecting Somali content creators. Operating across TikTok, Facebook, YouTube, and Instagram, we specialize in copyright enforcement that creators can afford. Our expert team has successfully handled hundreds of takedown cases, helping creators reclaim their digital rights and earnings.
            </p>
          </div>
          <div className="stats-grid reveal">
            <div className="stat-card">
              <p className="stat-number">500+</p>
              <p className="stat-label">Creators Protected</p>
            </div>
            <div className="stat-card">
              <p className="stat-number">1000+</p>
              <p className="stat-label">Takedowns Processed</p>
            </div>
            <div className="stat-card">
              <p className="stat-number">24hr</p>
              <p className="stat-label">Support Response Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing reveal-section">
        <div className="pricing-header">
          <span className="eyebrow reveal">Pricing</span>
          <h2 className="reveal">Affordable Protection</h2>
        </div>
        <div className="price-card reveal">
          <div className="price-card-top">
            <h4>Standard DMCA Takedown</h4>
            <p className="price-amount">$25 <span>per takedown</span></p>
          </div>
          <ul>
            <li>Professional DMCA documentation</li>
            <li>Direct platform submission</li>
            <li>Metadata & proof preservation</li>
            <li>Email confirmation updates</li>
            <li>Case reference number</li>
          </ul>
          <Link to="/contact" className="btn-primary">
            Report Now <span className="btn-arrow">&rarr;</span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner reveal-section">
          <div className="footer-platforms reveal">
            <h3>Trusted by Creators on</h3>
            <div className="footer-logo-grid">
              <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="footer-logo-link">
                <img src="/tiktok.png" alt="TikTok" />
              </a>
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="footer-logo-link">
                <img src="/facebook.png" alt="Facebook" />
              </a>
              <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="footer-logo-link">
                <img src="/youtube.png" alt="YouTube" />
              </a>
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="footer-logo-link">
                <img src="/instagram.png" alt="Instagram" />
              </a>
            </div>
          </div>
          <hr className="footer-divider" />
          <div className="footer-compliance reveal">
            <p>Licensed Company No. 0295800</p>
            <p>Somali Copyright Agency Ltd.</p>
            <p>Authorized DMCA Agent Filing</p>
            <p>Somalia Copyright Law No. 66 Compliant</p>
          </div>
          <div className="footer-bottom reveal">
            <p className="footer-disclaimer">Somali Copyright Agency Ltd. is a licensed copyright documentation and DMCA takedown service (Company No. 0295800), not a law firm. We provide documentation support under DMCA regulations.</p>
            <p className="footer-copyright">&copy; 2026 Somali Copyright Agency Ltd. — Company No. 0295800</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
